// ============================================================================
// OverlappingPoints Component (View)
// ============================================================================

import { useState, useRef, useEffect } from "react";
import {
	Box,
	Typography,
	Alert,
	IconButton,
	Paper,
	Divider,
	Button,
	Menu,
	MenuItem,
	ListSubheader,
	ListItemIcon,
	ListItemText,
} from "@mui/material";
import { Visibility, VisibilityOff, Delete, Circle } from "@mui/icons-material";
import { ConfirmDeleteDialog } from "../../components";
import { PortalTooltip } from "../../../../../../helpers";
import JobMap from "../../../../../../helpers/JobMap";
import type { OverlappingPointsProps } from "./OverlappingPoints.types";
import type { DuplicateGroup } from "../../WizardContainer";
import { useOverlappingPointsViewModel } from "./OverlappingPoints.vm";
import { getJobDisplayName } from "../../shared";
import "./OverlappingPoints.css";

/**
 * Component for displaying and resolving overlapping/duplicate point issues within a job
 */
export function OverlappingPoints(props: OverlappingPointsProps) {
	const vm = useOverlappingPointsViewModel(props);
	const [bulkMenuAnchor, setBulkMenuAnchor] = useState<HTMLElement | null>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const [bulkDeleteCompact, setBulkDeleteCompact] = useState(false);

	useEffect(() => {
		const el = panelRef.current;
		if (!el) return;
		const observer = new ResizeObserver(([entry]) => {
			setBulkDeleteCompact(entry.contentRect.width <= 400);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	// No issues state
	if (!vm.hasIssues) {
		return (
			<Box data-testid="no-issues-message" className="overlapping-points no-issues">
				<Alert severity="success">
					<Typography>No overlapping points detected. You can proceed to the next step.</Typography>
				</Alert>
			</Box>
		);
	}

	// No current issue (shouldn't happen but handle gracefully)
	if (!vm.currentIssue) {
		return null;
	}

	const jobName = getJobDisplayName(vm.currentIssue.job);
	const showBulkActions = vm.bulkDeleteByPosition.length > 0 || vm.bulkDeleteByFile.length > 0;

	return (
		<Box className="overlapping-points">
			{/* Issue Description */}
			<Box className="issue-description">
				<Typography variant="body2" color="text.secondary">
					<strong>{jobName}</strong> contains <strong>{vm.currentIssue.duplicateGroups.length}</strong> set(s) of
					overlapping points.
				</Typography>
			</Box>

			{/* Side-by-side layout: Content left, Map right */}
			<Box className="side-by-side-layout">
				{/* Left side - Duplicate Groups List */}
				<Box className="left-side">
					<Paper variant="outlined" className="groups-panel" ref={panelRef}>
						<Box className="groups-header">
							<Typography variant="subtitle2">Duplicate Groups</Typography>
							<Box className="visibility-controls">
								<IconButton size="small" onClick={vm.showAllFeatures}>
									<Visibility fontSize="small" />
								</IconButton>
								<IconButton size="small" onClick={vm.hideAllFeatures}>
									<VisibilityOff fontSize="small" />
								</IconButton>
								{showBulkActions && (
									<>
										{(() => {
											const btn = (
												<Button
													className="bulk-delete-button"
													size="small"
													color="error"
													variant="text"
													startIcon={<Delete />}
													onClick={(e) => setBulkMenuAnchor(e.currentTarget)}
													sx={{ textTransform: "none", minWidth: 0, mr: 0.5 }}
												>
													<span className="button-label bulk-delete-label">Bulk Delete</span>
												</Button>
											);
											return bulkDeleteCompact ? <PortalTooltip title="Bulk Delete">{btn}</PortalTooltip> : btn;
										})()}
										<Menu
											anchorEl={bulkMenuAnchor}
											open={Boolean(bulkMenuAnchor)}
											onClose={() => setBulkMenuAnchor(null)}
										>
											{vm.bulkDeleteByPosition.length > 0 && (
												<ListSubheader sx={{ lineHeight: "32px" }}>By Color</ListSubheader>
											)}
											{vm.bulkDeleteByPosition.map((option) => (
												<MenuItem
													key={option.key}
													onClick={() => {
														setBulkMenuAnchor(null);
														vm.handleBulkDeleteClick(option);
													}}
												>
													<ListItemIcon sx={{ minWidth: 28 }}>
														<Circle sx={{ fontSize: 14, color: option.color }} />
													</ListItemIcon>
													<ListItemText>
														{option.label} ({option.count})
													</ListItemText>
												</MenuItem>
											))}
											{vm.bulkDeleteByPosition.length > 0 && vm.bulkDeleteByFile.length > 0 && <Divider />}
											{vm.bulkDeleteByFile.length > 0 && (
												<ListSubheader sx={{ lineHeight: "32px" }}>By File</ListSubheader>
											)}
											{vm.bulkDeleteByFile.map((option) => (
												<MenuItem
													key={option.key}
													onClick={() => {
														setBulkMenuAnchor(null);
														vm.handleBulkDeleteClick(option);
													}}
												>
													<ListItemText>
														{option.label} ({option.count})
													</ListItemText>
												</MenuItem>
											))}
										</Menu>
									</>
								)}
							</Box>
						</Box>
						<Divider />

						<Box className="groups-list">
							{vm.currentIssue.duplicateGroups.map((group, groupIndex) => (
								<DuplicateGroupItem
									key={group.id}
									group={group}
									groupIndex={groupIndex}
									getPositionColor={vm.getPositionColor}
									hiddenFeatureIds={vm.hiddenFeatureIds}
									onToggleVisibility={(featureId) => vm.toggleFeatureVisibility(featureId)}
									onDeleteFeature={(featureIndex) => vm.handleDeleteFeatureClick(group.id, featureIndex, group.type)}
									getFeatureDisplayName={vm.getFeatureDisplayName}
								/>
							))}
						</Box>
					</Paper>
				</Box>

				{/* Right side - Map */}
				<Box className="right-side">
					<JobMap
						jobs={[vm.currentIssue.job]}
						colorMode="boundary-outline"
						duplicateFeatures={vm.mapFeatures}
						hiddenDuplicateFeatureIds={vm.hiddenFeatureIds}
						height="100%"
						hiddenJobIds={[]}
					/>
				</Box>
			</Box>

			{/* Delete Confirmation Dialog */}
			<ConfirmDeleteDialog
				open={vm.deleteConfirmation !== null}
				title={vm.deleteDialogTitle}
				message={vm.deleteDialogMessage}
				onConfirm={vm.handleConfirmDelete}
				onCancel={vm.handleCancelDelete}
			/>
		</Box>
	);
}

/**
 * Renders a single duplicate group with its features
 */
interface DuplicateGroupItemProps {
	group: DuplicateGroup;
	groupIndex: number;
	getPositionColor: (positionIndex: number) => string;
	hiddenFeatureIds: Set<string>;
	onToggleVisibility: (featureId: string) => void;
	onDeleteFeature: (featureIndex: number) => void;
	getFeatureDisplayName: (group: DuplicateGroup, index: number) => string;
}

function DuplicateGroupItem({
	group,
	groupIndex,
	getPositionColor,
	hiddenFeatureIds,
	onToggleVisibility,
	onDeleteFeature,
	getFeatureDisplayName,
}: DuplicateGroupItemProps) {
	return (
		<Box className="duplicate-group">
			<Box className="group-header">
				<Typography variant="body2" fontWeight={500}>
					Point Group #{groupIndex + 1}
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
					({group.features.length} duplicates)
				</Typography>
			</Box>
			<Box className="group-features">
				{group.features.map((feature, i) => {
					const featureIndex = group.featureIndices[i];
					const featureId = `${group.id}-${featureIndex}`;
					const isHidden = hiddenFeatureIds.has(featureId);
					const displayName = getFeatureDisplayName(group, i);
					// Extract filename from full path
					const filePath = feature.properties?.FilePath;
					const fileName = filePath ? filePath.split("/").pop() : null;

					return (
						<Box key={featureId} className="feature-item">
							<Box className="feature-info">
								<Box className="color-swatch-small" style={{ backgroundColor: getPositionColor(i) }} />
								<Typography variant="body2" className={isHidden ? "hidden-feature" : ""}>
									{displayName}
									{fileName && (
										<Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
											({fileName})
										</Typography>
									)}
								</Typography>
							</Box>
							<Box className="feature-actions">
								<IconButton size="small" onClick={() => onToggleVisibility(featureId)}>
									{isHidden ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
								</IconButton>
								<IconButton size="small" color="error" onClick={() => onDeleteFeature(featureIndex)}>
									<Delete fontSize="small" />
								</IconButton>
							</Box>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
}
